import {useCallback, useMemo} from 'react';
import {usePrimeCache} from '../Hooks/ServiceHooks';
import {service as SurveySubmissionStatusService} from '../Services/SurveySubmissionStatusService';
import {PRE_SUBMIT_STATUS_CODES} from './surveySubmissionUtils';

const STATUS_VARIANTS = {
    'ready-to-submit': 'warning',
    'in-review':       'info',
    'in-progress':     'secondary',
    'review-complete': 'success',
};

export const statusToVariant = (statusCode) => STATUS_VARIANTS[statusCode] ?? 'secondary';

// A submission stepped back from review shows this in place of its raw pre-submit
// status label ("Ready to Review"), which would otherwise read as a contradiction.
const CHANGES_REQUESTED_LABEL = 'Changes Requested';

/**
 * True when a submission was stepped back from review — it sits in a pre-submit
 * status but still carries a note. A reviewer (TFT) returning it and a supplier
 * withdrawing their own submission both land here and both read fine as
 * "Changes Requested", so the UI doesn't distinguish them.
 */
const isReturnedFromReview = (statusCode, hasNote) =>
    !!hasNote && PRE_SUBMIT_STATUS_CODES.has(statusCode);

// 'not-started' is shown role-specifically: to the supplier it's an actionable
// "Ready to Start" (it's their move), to the reviewer it's the neutral factual
// state. Same underlying status — only the label and emphasis differ.
const NOT_STARTED_PRESENTATION = {
    supplier: { label: 'Ready to Start', variant: 'warning' },
    reviewer: { label: 'Not Started',    variant: 'secondary' },
};

/**
 * The one status-presentation source. Returns a resolver
 *
 *     (statusCode, hasNote) => { label, variant } | null
 *
 * shared by the project-list badge and the survey widget's stepper so the two
 * agree on every label. Presentation priority:
 *   1. Returned from review (pre-submit + note) → "Changes Requested" in warning
 *      yellow, overriding the rest.
 *   2. not-started → role-specific: supplier "Ready to Start" (actionable),
 *      reviewer "Not Started" (neutral). isReviewer selects the side — its only use.
 *   3. Any other status → its display name from the cached status reference data
 *      and its mapped variant.
 *
 * A hook because branch 3 resolves the display name from the cached
 * SurveySubmissionStatus service (the server sends only the status code); calling
 * this primes that cache and re-renders the caller once it loads. Until the code
 * resolves, the resolver returns null — render nothing rather than a bare code.
 */
export const useSubmissionStatusPresentation = (isReviewer = false) => {
    const cacheLoaded = usePrimeCache(SurveySubmissionStatusService);
    return useCallback((statusCode, hasNote = false) => {
        if (isReturnedFromReview(statusCode, hasNote)) {
            return { label: CHANGES_REQUESTED_LABEL, variant: 'warning' };
        }
        if (statusCode === 'not-started') {
            return isReviewer ? NOT_STARTED_PRESENTATION.reviewer : NOT_STARTED_PRESENTATION.supplier;
        }
        const name = SurveySubmissionStatusService.CachedValueByProperty(statusCode, 'code')?.result?.name;
        return name ? { label: name, variant: statusToVariant(statusCode) } : null;
    }, [cacheLoaded, isReviewer]);
};

/**
 * The cached submission statuses in workflow order plus an ordinal resolver:
 *
 *     { statuses, indexOf }   where indexOf(code) → position, or -1 while the
 *                             cache is cold / for an unknown code
 *
 * The one client-side home of status-ordering mechanics — statuses arrive from
 * the reference service ordered by their sortOrder column, and every ordinal
 * comparison (stepper position, display floor) derives from this list. A hook
 * because the list lives in the cached status service; calling this primes
 * that cache and re-renders the caller once it loads.
 */
export const useOrderedSubmissionStatuses = () => {
    const cacheLoaded = usePrimeCache(SurveySubmissionStatusService);
    return useMemo(() => {
        const statuses = [...(SurveySubmissionStatusService.CachedValues()?.result ?? [])]
            .sort((a, b) => a.sortOrder - b.sortOrder);
        const indexByCode = new Map(statuses.map((s, idx) => [s.code, idx]));
        return { statuses, indexOf: (code) => indexByCode.get(code) ?? -1 };
    }, [cacheLoaded]);
};

/**
 * Builds the front-end URL for a specific CA's survey within a project context.
 * Returns null when there isn't enough information to build a real link.
 *
 *   contextNodeLink — the project context node's URL prefix (e.g. item.link).
 *
 * Centralized here so other callers (activity feed entries, table links, etc.)
 * can construct the same path without re-encoding the route shape.
 */
export const buildSurveyPath = (contextNodeLink, conservationActionId, surveyTypeCode) => {
    if (!conservationActionId || !contextNodeLink) return null;
    return `${contextNodeLink}/${conservationActionId}/${surveyTypeCode}`;
};

/**
 * Builds the { variant, label, href? } shape EditableContextNodeListItem
 * expects, from a per-project survey tag.
 *
 *   contextNodeLink — the project context node's URL prefix (item.link). When
 *                     present together with a conservationActionId on the tag,
 *                     the badge becomes a clickable link to that survey.
 *   presentStatus   — the resolver from useSubmissionStatusPresentation (it
 *                     carries the viewer role; a plain import can't, since the
 *                     display name lives in a cache only a hook can prime).
 */
export const surveyTagToBadge = (tag, contextNodeLink, presentStatus) => {
    if (!tag) return null;

    const presentation = presentStatus(tag.statusCode, tag.hasNote);
    if (!presentation) return null;
    const { label: statusLabel, variant } = presentation;

    return {
        variant,
        label: tag.surveyTypeName ? `${tag.surveyTypeName} — ${statusLabel}` : statusLabel,
        href: buildSurveyPath(contextNodeLink, tag.conservationActionId, tag.surveyTypeCode),
    };
};

/**
 * Builds the badge shape for a sub-program's actionable-projects rollup count.
 * Tag selection and the count itself are computed server-side (getForParent);
 * this only dresses the number for the sub-program row.
 *
 * Label is role-specific so each user sees their side of the workflow:
 *   - Reviewer:  "{count} to review"  (only in-review counts as actionable)
 *   - Supplier:  "{count} pending"    (not-started / in-progress / ready-to-submit)
 *
 * Both render in warning yellow — the rollup is consistently a "there's
 * something for you here" flag regardless of role.
 */
export const rollupCountToBadge = (count, isReviewer = false) => {
    if (!count) return null;
    return {
        variant: 'warning',
        label: isReviewer ? `${count} to review` : `${count} pending`,
    };
};
